import React from "react";
import { Box, TableCell } from "@mui/material";
import { TEXT_COLOR_PRIMARY } from "../../../constants/colors";
import { FONT_SIZE } from "../../../constants/fontSizes";

/**
 * 共通UI定義（Desktop / Mobile 共通）
 * - 色
 * - サイズ
 * - sxスタイル
 * - 小さなUIパーツ
 */

/**
 * テーブル全体で統一した配色を使うための定数
 */
export const COLORS = {
  border: "#D9D9D9",
  labelBg: "#E9EAFA",
  phaseHeaderBg: "#E9EAFA",
  text: TEXT_COLOR_PRIMARY,
  dotActive: "#1F1F1F",
  dotInactive: "#D0D0D8",
};

/** ===== サイズ定義 ===== */
export const SIZES = {
  radius: 10, // テーブル外枠の角丸
  dot: 7, // 工程ドットの直径
};

/** ===== 共通スタイル ===== */

/**
 * TableCell 共通ベーススタイル
 * - DesktopTable でのみ使用
 */
export const COMMON_CELL = {
  color: COLORS.text,
  fontSize: FONT_SIZE.XXS,
  borderBottom: `1px solid ${COLORS.border}`,
  verticalAlign: "middle",
  p: 0.5,
};

/**
 * ラベルセル用 sx
 * 左側の固定ラベル（プロジェクト名 / 規模 / 役割 など）
 */
export const sxLabel = () => ({
  ...COMMON_CELL,
  backgroundColor: COLORS.labelBg,
  fontWeight: 700,
  textAlign: "center",
  borderLeft: `1px solid ${COLORS.border}`,
  borderRight: `1px solid ${COLORS.border}`,
});

/**
 * 値セル用 sx
 * ラベルの右側に来るメイン情報
 */
export const sxValue = () => ({
  ...COMMON_CELL,
  whiteSpace: "pre-line",
});

/**
 * 工程ヘッダー（要件定義〜保守運用）
 */
export const sxPhaseHeader = () => ({
  ...COMMON_CELL,
  backgroundColor: COLORS.labelBg,
  borderLeft: `1px solid ${COLORS.border}`,
  fontWeight: 700,
  textAlign: "center",
  whiteSpace: "pre-line",
  p: 0,
});

/**
 * 工程ドット
 */
export const sxPhaseCell = () => ({
  ...COMMON_CELL,
  borderLeft: `1px solid ${COLORS.border}`,
});

/**
 * 共通UIコンポーネント
 * DesktopTable / MobileStack の両方で利用
 */

/**
 * ラベル用 TableCell
 * DesktopTable 専用
 */
export const LabelCell = ({ children, sx = {} }) => (
  <TableCell sx={{ ...sxLabel(), ...sx }}>{children}</TableCell>
);

/**
 * 値用 TableCell
 * - colSpan を受け取れる
 * - 改行表示対応
 */
export const ValueCell = ({ children, sx = {}, colSpan }) => (
  <TableCell sx={{ ...sxValue(), ...sx }} colSpan={colSpan}>
    {children}
  </TableCell>
);

/**
 * 工程ヘッダーセル（要件定義など）
 */
export const PhaseHeaderCell = ({ children, sx = {} }) => (
  <TableCell sx={{ ...sxPhaseHeader(), ...sx }}>{children}</TableCell>
);

/**
 * 工程
 */
export const PhaseDotCell = ({ children, sx = {} }) => (
  <TableCell sx={{ ...sxPhaseCell(), ...sx }}>{children}</TableCell>
);

/**
 * 工程ON/OFFを示すドット
 * Desktop / Mobile 共通
 */
export const Dot = ({ active }) => (
  <Box
    sx={{
      mx: "auto",
      width: SIZES.dot,
      height: SIZES.dot,
      borderRadius: "50%",
      backgroundColor: active ? COLORS.dotActive : COLORS.dotInactive,
    }}
  />
);

/**
 * Detail（業務内容 / 使用技術）ヘッダーセル
 */
export const DetailHeaderCell = ({
  children,
  withRightBorder = false,
  sx = {},
}) => (
  <Box
    sx={{
      p: 0.5,
      fontWeight: 700,
      fontSize: FONT_SIZE.XXS,
      textAlign: "center",
      backgroundColor: COLORS.labelBg,
      color: COLORS.text,
      borderRight: withRightBorder ? `1px solid ${COLORS.border}` : "none",
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * 値が空の場合に "-" を返す表示用フォーマッタ
 *
 * - null / undefined の場合は "-"
 * - 空文字 / 空白のみの場合も "-"
 * - 0 や false などは「値あり」としてそのまま返す
 *
 * 主にスキルシート画面の表示統一用（Mobile / Desktop 共通）
 */
export const formatValueOrDash = (value) => {
  if (value == null) return "-";

  const text = String(value).trim();
  return text !== "" ? value : "-";
};
